import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Download, RotateCcw, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUS_LABELS = { draft: "Borrador", pending_review: "Pendiente de revisión", approved: "Aprobada", rejected: "Rechazada" };
const TYPE_LABELS = { initiative: "Iniciativa", pilot: "Piloto", grant: "Ayuda" };
const EVENT_LABELS = {
  created: "Creada", updated: "Actualizada", sent_for_review: "Enviada a revisión",
  approved: "Aprobada", rejected: "Rechazada", resubmitted: "Reenviada",
};

export function ProposalDetailDialog({ open, onOpenChange, proposal: p, isReviewer, onDownload, onChanged }) {
  const { token } = useAuth();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  if (!p) return null;

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.decideProposal(token, p.id, decision, notes);
      toast.success(decision === "approved" ? "Propuesta aprobada" : "Propuesta rechazada");
      onChanged();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar la decisión");
    } finally {
      setBusy(false);
    }
  };

  const resubmit = async () => {
    setBusy(true);
    try {
      await api.resubmitProposal(token, p.id);
      toast.success("Propuesta reabierta como borrador — edítala y vuelve a enviarla");
      onChanged();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reabrir la propuesta");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="proposal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {p.title}
            <Badge variant="outline">{STATUS_LABELS[p.status]}</Badge>
          </DialogTitle>
          <DialogDescription>{p.linea_estrategica || "Sin línea estratégica"} · v{p.version} · por {p.created_by_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {p.linked_entities?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {p.linked_entities.map((e, i) => <Badge key={i} variant="secondary">{TYPE_LABELS[e.entity_type]}: {e.entity_name}</Badge>)}
            </div>
          )}
          {p.core_proyecto && <Section title="Core del proyecto" text={p.core_proyecto} />}
          {p.objetivos_beneficios && <Section title="Objetivos y beneficios esperados" text={p.objetivos_beneficios} />}
          {p.buenas_practicas && <Section title="Buenas prácticas" text={p.buenas_practicas} />}
          {(p.convocatoria?.organismo || p.convocatoria?.nombre) && (
            <Section title="Convocatoria vinculada" text={`${p.convocatoria.organismo || ""} · ${p.convocatoria.nombre || ""}\nPlazos: ${p.convocatoria.plazos || "—"} · Presupuesto: ${p.convocatoria.presupuesto_maximo || "—"}`} />
          )}
          {p.budget?.coste_total ? <Section title="Presupuesto" text={`${p.budget.coste_total} € · OPEX ${p.budget.opex_pct}% / CAPEX ${p.budget.capex_pct}%`} /> : null}
          {p.riesgos && <Section title="Riesgos" text={p.riesgos} />}
          {p.cronograma && <Section title="Cronograma" text={p.cronograma} />}
          {p.equipo_necesario && <Section title="Equipo necesario" text={p.equipo_necesario} />}

          {p.reviewer_notes && (
            <div className="rounded-md bg-muted p-3 text-xs">
              <span className="font-semibold">Notas del revisor: </span>{p.reviewer_notes}
            </div>
          )}

          {p.history?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Historial</div>
              <div className="space-y-1.5 max-h-32 overflow-y-auto" data-testid="proposal-history-list">
                {p.history.slice().reverse().map((h, i) => (
                  <div key={i} className="text-xs flex justify-between text-muted-foreground border-b last:border-0 pb-1">
                    <span>{EVENT_LABELS[h.type] || h.type} · {h.actor_name}</span>
                    <span>{new Date(h.timestamp).toLocaleString("es-ES")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isReviewer && p.status === "pending_review" && (
            <div className="space-y-2 border-t pt-3" data-testid="proposal-decision-panel">
              <Textarea placeholder="Notas de la decisión (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="proposal-decision-notes-input" />
              <div className="flex gap-2">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => decide("approved")} data-testid="proposal-approve-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Aprobar
                </Button>
                <Button variant="destructive" className="flex-1" disabled={busy} onClick={() => decide("rejected")} data-testid="proposal-reject-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}Rechazar
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => onDownload(p)} data-testid="proposal-detail-download-btn">
              <Download className="w-4 h-4 mr-2" />Descargar PDF
            </Button>
            {p.status === "rejected" && (
              <Button variant="outline" className="flex-1" disabled={busy} onClick={resubmit} data-testid="proposal-resubmit-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}Reabrir y editar
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, text }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">{title}</div>
      <div className="whitespace-pre-line text-sm">{text}</div>
    </div>
  );
}
